import React, { useState, useEffect, useMemo } from 'react';
import T from '../../components/common/T';
import { useParams, useNavigate } from 'react-router-dom';

// --- CONTEXT IMPORTS ---
import { useCostSheets } from '../../context/CostSheetContext';
import { useInventory } from '../../context/InventoryContext'; 
import { useEquipment } from '../../context/EquipmentContext'; 
import { useProjects } from '../../context/ProjectContext';
import { usePurchaseOrder } from '../../context/PurchaseOrderContext'; 
import Price from "../../components/common/Price";
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldRenderer from '../../components/common/CustomFieldRenderer';
// --- ICONS ---
import { 
  ArrowLeftIcon, 
  UserGroupIcon,
  TruckIcon,
  BuildingOfficeIcon, 
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  LockClosedIcon,
  CheckBadgeIcon,
  TrashIcon 
} from '@heroicons/react/24/outline';

// --- ODOO STYLE SMART BUTTON ---
const SmartButton = ({ icon: Icon, label, count, onClick }) => (
  <button 
    onClick={onClick} 
    className="flex items-center gap-3 bg-white border border-gray-300 px-4 py-2 hover:bg-gray-50 transition min-w-[160px] shadow-sm first:rounded-l-md last:rounded-r-md -ml-px first:ml-0"
  >
    <div className="p-2 bg-purple-50 rounded text-purple-700">
        <Icon className="w-5 h-5" />
    </div>
    <div className="text-left">
      <div className="text-lg font-bold text-gray-800 leading-none">{count}</div>
      <div className="text-[10px] uppercase font-bold text-gray-500 mt-1">{label}</div>
    </div>
  </button>
);

export default function CostSheetDetail() {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Materials');
  const { customData, setCustomData, saveCustomFields } = useCustomFields('costing', id || null);

  // --- CONTEXT DATA ---
  const { 
      costSheets, 
      updateCostSheetStatus, 
      getSheetDetails, // 🟢 Get Data from DB
      addCostMaterial, // 🟢 Add to DB
      addCostEquipment // 🟢 Add to DB
  } = useCostSheets();

  const { inventories } = useInventory(); // For Dropdowns
  const { equipmentList } = useEquipment(); // For Dropdowns
  const { purchaseOrders } = usePurchaseOrder(); // Keeping POs for "Expenses" tab for now
  const { getProjectById } = useProjects();

  // --- LOCAL STATE FOR DATABASE DATA ---
  const [dbData, setDbData] = useState({ 
      materials: [], 
      labor: [], 
      equipment: [], 
      scrap: [] 
  });
  const [isLoading, setIsLoading] = useState(true);

  // 🟢 LOAD DATA FROM DB
  const refreshData = async () => {
      if (!id) return;
      setIsLoading(true);
      const data = await getSheetDetails(id);
      setDbData(data);
      setIsLoading(false);
  };

  useEffect(() => {
      refreshData();
  }, [id]);

  // --- PROJECT INFO ---
  const sheet = costSheets.find(s => String(s.id) === String(id));
  const projectLink = sheet ? getProjectById(sheet.projectId) : null;
  const displayProjectName = sheet?.projectName || projectLink?.name || "Unknown Project";

  // --- DERIVED DATA (Mapped from DB Columns) ---
  
  // 1. Materials
  const sheetMaterials = dbData.materials.map(m => ({
      ...m,
      date: m.date,
      materialName: m.material_name || m.materialName,
      quantity: Number(m.quantity),
      unitPrice: Number(m.unit_price || m.unitPrice),
      totalPrice: Number(m.total_price || m.totalPrice),
      source: m.source
  }));

  // 2. Labor
  const sheetLabor = dbData.labor.map(l => ({
      ...l,
      date: l.date,
      employeeName: l.employee_name || l.employeeName,
      description: l.description,
      hours: Number(l.hours),
      rate: Number(l.hourly_rate || l.hourlyRate),
      cost: Number(l.total_cost || l.totalCost)
  }));

  // 3. Equipment
  const sheetEquipment = dbData.equipment.map(e => ({
      ...e,
      date: e.date,
      equipmentName: e.equipment_name || e.equipmentName,
      description: e.description,
      hours: Number(e.hours),
      hourlyCost: Number(e.hourly_rate || e.hourlyRate),
      totalCost: Number(e.total_cost || e.totalCost)
  }));

  // 4. Scrap
  const sheetScrap = dbData.scrap.map(s => ({
      ...s,
      date: s.date,
      productName: s.item_name || s.itemName,
      quantity: Number(s.quantity),
      cost: Number(s.estimated_loss || s.estimatedLoss)
  }));

  // 5. Expenses (Still from POs as requested, strictly filtered)
  // You can move this to DB later if needed, but keeping logic consistent with previous step
  const sheetExpenses = useMemo(() => {
      if (!sheet) return [];
      return purchaseOrders
        .filter(po => String(po.projectId) === String(sheet.projectId) && ['Completed', 'Received'].includes(po.status) && !inventories.some(i => i.itemName === po.materialName))
        .map(po => ({ ...po, type: 'Indirect' }));
  }, [purchaseOrders, sheet, inventories]);


  // ================= 🟢 FINANCIAL ENGINE =================
  const financials = useMemo(() => {
    const mat = sheetMaterials.reduce((sum, m) => sum + m.totalPrice, 0);
    const lab = sheetLabor.reduce((sum, l) => sum + l.cost, 0);
    const eq = sheetEquipment.reduce((sum, e) => sum + e.totalCost, 0);
    const scr = sheetScrap.reduce((sum, s) => sum + s.cost, 0);
    const exp = sheetExpenses.reduce((sum, ex) => sum + (Number(ex.totalPrice) || 0), 0);

    return {
      material: mat,
      labor: lab,
      equipment: eq,
      scrap: scr,
      expense: exp,
      totalActual: mat + lab + eq + scr + exp
    };
  }, [sheetMaterials, sheetLabor, sheetEquipment, sheetScrap, sheetExpenses]);

  // --- MODAL STATES ---
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [newMaterial, setNewMaterial] = useState({ materialId: '', quantity: 1, unitPrice: 0 });
  
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] = useState(false);
  const [newEquipmentLog, setNewEquipmentLog] = useState({ equipmentId: '', hours: 1, date: new Date().toISOString().split('T')[0], description: '' });

  // --- HANDLERS (Connected to DB) ---
  const handleSaveMaterial = async (e) => {
    e.preventDefault();
    const realItem = inventories.find(i => String(i.id) === String(newMaterial.materialId));
    if (!realItem) return;

    // 🟢 Save to DB
    await addCostMaterial({
      costSheetId: id,
      projectId: sheet.projectId,
      materialName: realItem.itemName,
      quantity: Number(newMaterial.quantity),
      unitPrice: Number(newMaterial.unitPrice),
      totalPrice: Number(newMaterial.quantity) * Number(newMaterial.unitPrice),
      date: new Date().toISOString().split('T')[0],
      source: "Manual Entry"
    });

    refreshData(); // 🟢 Refresh Table
    setIsMaterialModalOpen(false);
    setNewMaterial({ materialId: '', quantity: 1, unitPrice: 0 });
  };

  const handleSaveEquipment = async (e) => {
    e.preventDefault();
    const asset = equipmentList.find(eq => String(eq.id) === String(newEquipmentLog.equipmentId));
    if (!asset) return;

    const rate = Number(asset.hourlyCost || 0);
    const cost = Number(newEquipmentLog.hours) * rate;

    // 🟢 Save to DB
    await addCostEquipment({
      costSheetId: id,
      projectId: sheet.projectId,
      equipmentName: asset.equipmentName || asset.name,
      description: newEquipmentLog.description,
      hours: Number(newEquipmentLog.hours),
      hourlyRate: rate,
      totalCost: cost,
      date: newEquipmentLog.date
    });

    refreshData(); // 🟢 Refresh Table
    setIsEquipmentModalOpen(false);
    setNewEquipmentLog({ equipmentId: '', hours: 1, date: new Date().toISOString().split('T')[0], description: '' });
  };

  if (!sheet || isLoading) return <div className="p-10 text-center"><T>Loading Cost Sheet Data...</T></div>;

  return (
    <>
      <div className="p-6 bg-gray-100 min-h-screen font-sans">
        
        {/* --- TOP ACTIONS BAR --- */}
        <div className="flex justify-between items-center mb-4">
             <div className="flex gap-2">
                {sheet.status === 'Draft' && <button onClick={() => updateCostSheetStatus(id, "Confirmed")} className="px-4 py-2 text-xs font-bold uppercase rounded shadow-sm text-white bg-purple-700 hover:bg-purple-800 transition"><T>Confirm</T></button>}
                {sheet.status === 'Confirmed' && <button onClick={() => updateCostSheetStatus(id, "Approved")} className="px-4 py-2 text-xs font-bold uppercase rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 transition"><T>Approve</T></button>}
                {sheet.status === 'Approved' && <button onClick={() => updateCostSheetStatus(id, "Done")} className="px-4 py-2 text-xs font-bold uppercase rounded shadow-sm text-white bg-green-600 hover:bg-green-700 transition flex items-center gap-1"><CheckBadgeIcon className="w-4 h-4"/><T> Lock / Done</T></button>}
                {sheet.status === 'Done' && <span className="px-4 py-2 text-xs font-bold uppercase rounded bg-green-100 text-green-700 border border-green-200 flex items-center gap-1"><LockClosedIcon className="w-4 h-4"/><T> Cost Sheet Locked</T></span>}
                {sheet.status !== 'Done' && <button onClick={() => navigate(-1)} className="px-4 py-2 text-xs font-bold uppercase bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"><T>Back</T></button>}
             </div>

             <div className="flex bg-white rounded shadow-sm overflow-hidden border border-gray-300">
                {['Draft', 'Confirmed', 'Approved', 'Done'].map(step => (
                    <div key={step} className={`px-4 py-2 text-xs font-bold uppercase border-r last:border-0 relative ${sheet.status === step ? 'bg-purple-700 text-white' : 'text-gray-500 bg-gray-50'}`}>{step}</div>
                ))}
             </div>
        </div>

        {/* --- MAIN CARD --- */}
        <div className="bg-white border border-gray-300 shadow-sm rounded-lg min-h-[600px]">
           
           {/* SMART BUTTONS */}
           <div className="flex justify-end p-2 border-b border-gray-100 bg-gray-50/50">
              <SmartButton icon={TrashIcon} label="Scrap / Waste" count={sheetScrap.length} onClick={() => setActiveTab('Scrap')} />
              <SmartButton icon={CurrencyDollarIcon} label="Expenses" count={sheetExpenses.length} onClick={() => setActiveTab('Expenses')} />
              <SmartButton icon={UserGroupIcon} label="HR Timesheets" count={sheetLabor.length} onClick={() => setActiveTab('Labours')} />
              <SmartButton icon={WrenchScrewdriverIcon} label="Equipment" count={sheetEquipment.length} onClick={() => setActiveTab('Equipment')} />
              <SmartButton icon={DocumentTextIcon} label="Cost Lines" count={sheetMaterials.length + sheetLabor.length + sheetEquipment.length + sheetExpenses.length + sheetScrap.length} onClick={() => {}} />
           </div>

           {/* HEADER INFO */}
           <div className="p-8 pb-4">
              <div className="flex justify-between items-start mb-6">
                 <div>
                    <h1 className="text-xl font-normal text-gray-800 mb-1">{sheet.name}</h1>
                    <p className="text-sm text-gray-500">ID: {sheet.id}</p>
                 </div>
                 <div className="text-right">
                    <p className="text-xs text-gray-400 uppercase font-bold"><T>Total Cost</T></p>
                    <h2 className="text-base font-bold text-gray-800"><Price amount={financials.totalActual} /></h2>
                 </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-8 text-sm border-t border-gray-100 pt-6">
                 <div><span className="block text-gray-500 font-bold text-xs uppercase mb-1"><T>Project</T></span><span className="text-purple-700 font-medium hover:underline cursor-pointer">{displayProjectName}</span></div>
                 <div><span className="block text-gray-500 font-bold text-xs uppercase mb-1"><T>Status</T></span><span className="text-gray-800">{sheet.status}</span></div>
              </div>
           </div>

           {/* --- TABS --- */}
           <div className="mt-6">
              <div className="px-8 border-b border-gray-200 flex gap-8">
                 {['Materials', 'Labours', 'Equipment', 'Expenses', 'Scrap', 'Budget'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-sm font-bold uppercase tracking-wide border-b-2 transition ${activeTab === tab ? 'border-purple-700 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{tab}</button>
                 ))}
              </div>

              <div className="p-0">
                  
                  {/* 1. MATERIALS TAB */}
                  {activeTab === 'Materials' && (
                     <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                           <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-xs border-b border-gray-200">
                              <tr>
                                 <th className="px-6 py-3"><T>Date</T></th>
                                 <th className="px-6 py-3"><T>Product</T></th>
                                 <th className="px-6 py-3"><T>Source</T></th>
                                 <th className="px-6 py-3 text-right"><T>Qty</T></th>
                                 <th className="px-6 py-3 text-right"><T>Cost</T></th>
                                 <th className="px-6 py-3 text-right bg-yellow-50 text-yellow-800 border-l border-yellow-100"><T>Total</T></th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100">
                              {sheetMaterials.map((m, idx) => (
                                 <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-6 py-3">{new Date(m.date || Date.now()).toLocaleDateString()}</td>
                                    <td className="px-6 py-3 font-medium text-gray-800">{m.materialName}</td>
                                    <td className="px-6 py-3">
                                        <span className={`text-[10px] px-2 py-1 rounded font-bold uppercase ${m.source === 'Purchase' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-green-100 text-green-700'}`}>{m.source || 'Stock'}</span>
                                    </td>
                                    <td className="px-6 py-3 text-right text-gray-400">{m.quantity}</td>
                                    <td className="px-6 py-3 text-right"><Price amount={m.unitPrice} /></td>
                                    <td className="px-6 py-3 text-right font-bold bg-yellow-50 border-l border-yellow-100"><Price amount={m.totalPrice} /></td>
                                 </tr>
                              ))}
                              <tr><td colSpan="6" className="px-6 py-3"><button onClick={() => setIsMaterialModalOpen(true)} className="text-purple-700 font-bold text-xs uppercase hover:underline"><T>+ Add Manual Entry</T></button></td></tr>
                           </tbody>
                        </table>
                     </div>
                  )}

                  {/* 2. SCRAP TAB */}
                  {activeTab === 'Scrap' && (
                     <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                           <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-xs border-b border-gray-200">
                              <tr>
                                 <th className="px-6 py-3"><T>Date</T></th>
                                 <th className="px-6 py-3"><T>Product</T></th>
                                 <th className="px-6 py-3"><T>Reason</T></th>
                                 <th className="px-6 py-3 text-right"><T>Qty</T></th>
                                 <th className="px-6 py-3 text-right bg-red-50 text-red-800 border-l border-red-100"><T>Value Loss</T></th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100">
                              {sheetScrap.map((s, idx) => (
                                 <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-6 py-3">{new Date(s.date).toLocaleDateString()}</td>
                                    <td className="px-6 py-3 font-medium text-gray-900">{s.productName}</td>
                                    <td className="px-6 py-3 text-gray-500 italic">{s.reason}</td>
                                    <td className="px-6 py-3 text-right font-mono text-gray-600">-{s.quantity}</td>
                                    <td className="px-6 py-3 text-right font-bold text-red-600 bg-red-50 border-l border-red-100"><Price amount={s.cost} /></td>
                                 </tr>
                              ))}
                              {sheetScrap.length === 0 && (
                                  <tr><td colSpan="5" className="p-6 text-center text-gray-400"><T>No scrap recorded.</T></td></tr>
                              )}
                           </tbody>
                           <tfoot className="bg-gray-50 border-t border-gray-200 font-bold text-gray-800">
                                <tr><td colSpan="4" className="px-6 py-3 text-right"><T>Total Scrap Loss:</T></td><td className="px-6 py-3 text-right text-red-600"><Price amount={financials.scrap} /></td></tr>
                           </tfoot>
                        </table>
                     </div>
                  )}

                  {/* 3. LABOR TAB */}
                  {activeTab === 'Labours' && (
                     <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                           <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-xs border-b border-gray-200">
                              <tr>
                                 <th className="px-6 py-3"><T>Date</T></th>
                                 <th className="px-6 py-3"><T>Employee</T></th>
                                 <th className="px-6 py-3"><T>Description</T></th>
                                 <th className="px-6 py-3 text-right"><T>Hours</T></th>
                                 <th className="px-6 py-3 text-right"><T>Rate</T></th>
                                 <th className="px-6 py-3 text-right bg-yellow-50 text-yellow-800 border-l border-yellow-100"><T>Cost</T></th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100">
                              {sheetLabor.map((l, idx) => (
                                 <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-6 py-3">{new Date(l.date || Date.now()).toLocaleDateString()}</td>
                                    <td className="px-6 py-3 font-medium text-gray-800">{l.employeeName}</td>
                                    <td className="px-6 py-3 text-gray-500">{l.description || "Work Log"}</td>
                                    <td className="px-6 py-3 text-right font-mono">{l.hours}</td>
                                    <td className="px-6 py-3 text-right text-gray-500"><Price amount={l.rate} /></td>
                                    <td className="px-6 py-3 text-right font-bold bg-yellow-50 border-l border-yellow-100"><Price amount={l.cost} /></td>
                                 </tr>
                              ))}
                              {sheetLabor.length === 0 && (
                                  <tr><td colSpan="6" className="p-6 text-center text-gray-400"><T>No labor hours logged yet.</T></td></tr>
                              )}
                           </tbody>
                           <tfoot className="bg-gray-50 border-t border-gray-200 font-bold text-gray-800">
                                <tr><td colSpan="5" className="px-6 py-3 text-right"><T>Total Labor Cost:</T></td><td className="px-6 py-3 text-right"><Price amount={financials.labor} /></td></tr>
                           </tfoot>
                        </table>
                     </div>
                  )}

                  {/* 4. EQUIPMENT TAB */}
                  {activeTab === 'Equipment' && (
                     <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                           <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-xs border-b border-gray-200">
                              <tr>
                                 <th className="px-6 py-3"><T>Date</T></th>
                                 <th className="px-6 py-3"><T>Asset</T></th>
                                 <th className="px-6 py-3"><T>Description</T></th>
                                 <th className="px-6 py-3 text-right"><T>Hours</T></th>
                                 <th className="px-6 py-3 text-right"><T>Rate</T></th>
                                 <th className="px-6 py-3 text-right bg-yellow-50 text-yellow-800 border-l border-yellow-100"><T>Cost</T></th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100">
                              {sheetEquipment.map((e, idx) => (
                                 <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-6 py-3">{new Date(e.date).toLocaleDateString()}</td>
                                    <td className="px-6 py-3 font-medium text-gray-800">{e.equipmentName}</td>
                                    <td className="px-6 py-3 text-gray-500">{e.description}</td>
                                    <td className="px-6 py-3 text-right font-mono">{e.hours}</td>
                                    <td className="px-6 py-3 text-right text-gray-500"><Price amount={e.hourlyCost} /></td>
                                    <td className="px-6 py-3 text-right font-bold bg-yellow-50 border-l border-yellow-100"><Price amount={e.totalCost} /></td>
                                 </tr>
                              ))}
                              <tr><td colSpan="6" className="px-6 py-3"><button onClick={() => setIsEquipmentModalOpen(true)} className="text-purple-700 font-bold text-xs uppercase hover:underline"><T>+ Add Usage Log</T></button></td></tr>
                           </tbody>
                           <tfoot className="bg-gray-50 border-t border-gray-200 font-bold text-gray-800">
                                <tr><td colSpan="5" className="px-6 py-3 text-right"><T>Total Equipment Cost:</T></td><td className="px-6 py-3 text-right"><Price amount={financials.equipment} /></td></tr>
                           </tfoot>
                        </table>
                     </div>
                  )}

                  {/* 5. EXPENSES TAB */}
                  {activeTab === 'Expenses' && (
                     <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                           <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-xs border-b border-gray-200">
                              <tr>
                                 <th className="px-6 py-3"><T>Date</T></th>
                                 <th className="px-6 py-3"><T>Expense Item</T></th>
                                 <th className="px-6 py-3"><T>Category</T></th>
                                 <th className="px-6 py-3 text-right"><T>Amount</T></th>
                                 <th className="px-6 py-3 text-right bg-yellow-50 text-yellow-800 border-l border-yellow-100"><T>Actual Cost</T></th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-gray-100">
                              {sheetExpenses.map((ex, idx) => (
                                 <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-6 py-3">{new Date(ex.date || ex.createdAt).toLocaleDateString()}</td>
                                    <td className="px-6 py-3 font-medium text-gray-800">{ex.materialName}</td>
                                    <td className="px-6 py-3 text-gray-500">{ex.vendorName}</td>
                                    <td className="px-6 py-3 text-right text-gray-500"><Price amount={ex.unitPrice} /></td>
                                    <td className="px-6 py-3 text-right font-bold bg-yellow-50 border-l border-yellow-100"><Price amount={Number(ex.totalPrice)} /></td>
                                 </tr>
                              ))}
                              {sheetExpenses.length === 0 && (
                                  <tr><td colSpan="5" className="p-6 text-center text-gray-400"><T>No indirect expenses logged.</T></td></tr>
                              )}
                           </tbody>
                           <tfoot className="bg-gray-50 border-t border-gray-200 font-bold text-gray-800">
                                <tr><td colSpan="4" className="px-6 py-3 text-right"><T>Total Expenses:</T></td><td className="px-6 py-3 text-right"><Price amount={financials.expense} /></td></tr>
                           </tfoot>
                        </table>
                     </div>
                  )}

                  {/* 6. BUDGET TAB */}
                  {activeTab === 'Budget' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-4 px-6 pb-6">
                         <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h3 className="text-gray-500 text-xs font-bold uppercase mb-4 tracking-wider"><T>Cost Breakdown</T></h3>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center pb-2 border-b border-gray-50">
                                    <span className="text-gray-600"><T>Material Cost</T></span> 
                                    <span className="font-bold text-gray-800"><Price amount={financials.material} /></span>
                                </div>
                                <div className="flex justify-between items-center pb-2 border-b border-gray-50">
                                    <span className="text-gray-600"><T>Labour Cost</T></span> 
                                    <span className="font-bold text-gray-800"><Price amount={financials.labor} /></span>
                                </div>
                                <div className="flex justify-between items-center pb-2 border-b border-gray-50">
                                    <span className="text-gray-600"><T>Equipment Cost</T></span> 
                                    <span className="font-bold text-gray-800"><Price amount={financials.equipment} /></span>
                                </div>
                                <div className="flex justify-between items-center pb-2 border-b border-gray-50">
                                    <span className="text-gray-600 flex items-center gap-2"><T>Project Expenses </T><span className="text-[10px] bg-yellow-100 text-yellow-800 px-1.5 rounded font-bold"><T>Indirect</T></span></span> 
                                    <span className="font-bold text-gray-800"><Price amount={financials.expense} /></span>
                                </div>
                                <div className="flex justify-between items-center pb-2 border-b border-gray-50">
                                    <span className="text-red-600 font-medium flex items-center gap-2"><T>Scrap / Wastage </T><span className="text-[10px] bg-red-100 text-red-800 px-1.5 rounded font-bold"><T>Loss</T></span></span> 
                                    <span className="font-bold text-red-600"><Price amount={financials.scrap} /></span>
                                </div>
                                <div className="flex justify-between items-center pt-2 text-purple-700 text-lg">
                                    <span className="font-bold"><T>Total Actual Cost</T></span> 
                                    <span className="font-extrabold"><Price amount={financials.totalActual} /></span>
                                </div>
                            </div>
                         </div>
                      </div>
                  )}

              </div>
           </div>
        </div>

        {/* MODALS */}
        {isMaterialModalOpen && (<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"><div className="bg-white p-6 rounded shadow-xl w-96 animate-fade-in"><h3 className="font-bold text-base mb-4"><T>Add Material Cost</T></h3><form onSubmit={handleSaveMaterial} className="space-y-4"><select required className="w-full border p-2 rounded" onChange={e => setNewMaterial({...newMaterial, materialId: e.target.value})}><option value=""><T>Select Product</T></option>{inventories.map(i => <option key={i.id} value={i.id}>{i.itemName}</option>)}</select><input required type="number" placeholder="Qty" className="w-full border p-2 rounded" onChange={e => setNewMaterial({...newMaterial, quantity: e.target.value})} /><input required type="number" placeholder="Unit Price" className="w-full border p-2 rounded" onChange={e => setNewMaterial({...newMaterial, unitPrice: e.target.value})} /><div className="flex justify-end gap-2"><button type="button" onClick={() => setIsMaterialModalOpen(false)} className="px-4 py-2 text-gray-500"><T>Cancel</T></button><CustomFieldRenderer module="costing" customData={customData} onChange={setCustomData} />
            <button type="submit" className="px-4 py-2 bg-purple-700 text-white rounded"><T>Save</T></button></div></form></div></div>)}
        {isEquipmentModalOpen && (<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"><div className="bg-white p-6 rounded shadow-xl w-96 animate-fade-in"><h3 className="font-bold text-base mb-4"><T>Log Equipment Usage</T></h3><form onSubmit={handleSaveEquipment} className="space-y-4"><select required className="w-full border p-2 rounded" value={newEquipmentLog.equipmentId} onChange={e => setNewEquipmentLog({...newEquipmentLog, equipmentId: e.target.value})}><option value=""><T>Select Asset</T></option>{equipmentList.map(eq => <option key={eq.id} value={eq.id}>{eq.equipmentName || eq.name} - <Price amount={eq.hourlyCost} />/hr</option>)}</select><input type="date" required className="w-full border p-2 rounded" value={newEquipmentLog.date} onChange={e => setNewEquipmentLog({...newEquipmentLog, date: e.target.value})} /><input type="number" required min="0.5" className="w-full border p-2 rounded" value={newEquipmentLog.hours} onChange={e => setNewEquipmentLog({...newEquipmentLog, hours: e.target.value})} /><input type="text" className="w-full border p-2 rounded" placeholder="Desc" value={newEquipmentLog.description} onChange={e => setNewEquipmentLog({...newEquipmentLog, description: e.target.value})} /><div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setIsEquipmentModalOpen(false)} className="px-4 py-2 text-gray-500 rounded"><T>Cancel</T></button><button type="submit" className="px-4 py-2 bg-purple-700 text-white rounded"><T>Save</T></button></div></form></div></div>)}

      </div>
    </>
  );
}